import { useCallback, useEffect, useId, useRef, useState, type KeyboardEvent } from 'react';
import { BiCheck, BiPencil, BiPlus } from 'react-icons/bi';

import { useLongPress } from '@/hooks/useLongPress';
import {
  CHECKLIST_ITEMS_MAX,
  CHECKLIST_TEXT_MAX,
  type ChecklistItem,
} from '@/store/tasks/tasks.dto';
import { toast } from '@/utils/toast';

import AddButton from '../Button/AddButton';
import ContextMenu from '../ContextMenu/ContextMenu';
import Checkbox from '../Inputs/Checkbox';
import { ProgressBar } from '../ProgressBar';

import { newDraft, type ChecklistDraft } from './checklistDrafts';
import s from './TaskChecklist.module.scss';

const StepRow = ({
  id,
  item,
  editable,
  onToggle,
  onMenu,
}: {
  id: string;
  item: ChecklistItem;
  editable: boolean;
  onToggle: () => void;
  /** Rename and Delete, on a press and hold or a right-click. Without it the row only ticks. */
  onMenu?: (anchor: HTMLElement) => void;
}) => {
  const rowRef = useRef<HTMLDivElement>(null);
  const openMenu = useCallback(() => {
    if (rowRef.current) onMenu?.(rowRef.current);
  }, [onMenu]);
  // The hold also swallows the click that ends it, so opening the menu never ticks the box.
  const press = useLongPress(openMenu);

  return (
    <div
      ref={rowRef}
      className={s.row}
      {...(onMenu && {
        ...press,
        onContextMenu: (e: React.MouseEvent) => {
          e.preventDefault();
          openMenu();
        },
      })}
    >
      <Checkbox
        id={id}
        label={item.text}
        checked={item.status === 'done'}
        disabled={!editable}
        strikethrough
        className={s.check}
        onChange={onToggle}
      />
    </div>
  );
};

/** One line of text with a save button, for a new step or a renamed one. */
const StepInput = ({
  icon,
  value,
  placeholder,
  keepsOpen,
  onChange,
  onSave,
  onCancel,
}: {
  icon: React.ReactNode;
  value: string;
  placeholder: string;
  /** Enter and the save button leave the field open for the next one (adding a step). */
  keepsOpen: boolean;
  onChange: (value: string) => void;
  /** `keepOpen` is true for Enter and the save button, false when focus leaves. */
  onSave: (keepOpen: boolean) => void;
  onCancel: () => void;
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  // Esc unmounts the field, and a focused field that goes away still reports a blur —
  // which must not save what Esc just threw away.
  const cancelled = useRef(false);

  // A field that closes on save saves through its blur alone, so the blur its own
  // unmounting reports cannot save it a second time.
  const save = () => (keepsOpen ? onSave(true) : inputRef.current?.blur());

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.nativeEvent.isComposing) return;
    if (e.key === 'Enter') {
      e.preventDefault();
      save();
    } else if (e.key === 'Escape') {
      // The Modal listens for Escape on document and would close itself.
      e.preventDefault();
      e.stopPropagation();
      cancelled.current = true;
      onCancel();
    }
  };

  return (
    <div className={s.inputRow}>
      <span className={s.inputIcon} aria-hidden="true">
        {icon}
      </span>
      <input
        ref={inputRef}
        className={s.input}
        value={value}
        placeholder={placeholder}
        maxLength={CHECKLIST_TEXT_MAX}
        enterKeyHint={keepsOpen ? 'next' : 'done'}
        aria-label="Step"
        autoFocus
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={handleKeyDown}
        onBlur={() => {
          if (!cancelled.current) onSave(false);
        }}
      />
      {value.trim() && (
        <button
          type="button"
          className={s.save}
          aria-label="Save Step"
          // Keeps focus, and the phone keyboard, in the field.
          onMouseDown={(e) => e.preventDefault()}
          onClick={save}
        >
          <span className={s.saveDisc}>
            <BiCheck size={20} aria-hidden="true" />
          </span>
        </button>
      )}
    </div>
  );
};

/**
 * A task's checklist as the task shows it: progress on top, every step in the order it
 * was written, done ones struck through in place rather than moved. With `onChange` the
 * steps tick and, unless `tickOnly`, a step is added at the foot, and pressing and holding
 * one (right-click on a desktop) offers Rename and Delete. Without it the list is read-only.
 *
 * Reordering stays in the task sheet (`ChecklistEditor`): a drag handle on every row
 * would turn the task into an edit form.
 *
 * @alias checklist, steps, subtasks, to-do list
 */
export const TaskChecklist = ({
  items,
  onChange,
  tickOnly = false,
}: {
  /** Drafts, so a step keeps its row while its save is out and before it has an id. */
  items: ChecklistDraft[];
  /** Receives the whole list after every change; `useChecklistAutosave` saves it. */
  onChange?: (next: ChecklistDraft[]) => void;
  /** For someone on the task who may tick its steps but not add, rename or delete them. */
  tickOnly?: boolean;
}) => {
  const uid = useId();
  const editable = !!onChange;
  const canEditSteps = editable && !tickOnly;
  const done = items.filter((item) => item.status === 'done').length;

  const [menu, setMenu] = useState<{ draftId: string; anchor: HTMLElement } | null>(null);
  const [renaming, setRenaming] = useState<{ draftId: string; text: string } | null>(null);
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState('');

  // Undo can be pressed after more changes have landed; it restores into the list as it
  // is then, not as it was when the step went.
  const latest = useRef(items);
  useEffect(() => {
    latest.current = items;
  }, [items]);

  // Each step added scrolls the field back into view, above the phone keyboard.
  const addRowRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (adding) addRowRef.current?.scrollIntoView({ block: 'nearest' });
  }, [adding, items.length]);

  // The menu closes on the press that lands outside it, and the click that press ends in
  // would otherwise tick whichever step was under the thumb.
  const menuClosedAt = useRef(0);
  const closeMenu = useCallback(() => {
    menuClosedAt.current = Date.now();
    setMenu(null);
  }, []);
  const swallowDismissClick = (e: React.MouseEvent) => {
    if (Date.now() - menuClosedAt.current > 600) return;
    menuClosedAt.current = 0;
    e.preventDefault();
    e.stopPropagation();
  };

  const textOf = (draftId: string) => items.find((item) => item.draftId === draftId)?.text;

  const toggle = (draftId: string) =>
    onChange?.(
      items.map((item) =>
        item.draftId === draftId
          ? { ...item, status: item.status === 'done' ? 'open' : 'done' }
          : item,
      ),
    );

  const addStep = (keepOpen: boolean) => {
    const text = draft.trim();
    if (text) onChange?.([...items, newDraft({ text, status: 'open' })]);
    setDraft('');
    setAdding(keepOpen && !!text && items.length + 1 < CHECKLIST_ITEMS_MAX);
  };

  const saveRename = () => {
    if (!renaming) return;
    const text = renaming.text.trim();
    const { draftId } = renaming;
    // An emptied name keeps the old one rather than leaving a blank step; a step gone from the
    // list meanwhile is not brought back by its rename.
    const was = textOf(draftId);
    if (text && was !== undefined && text !== was) {
      onChange?.(items.map((item) => (item.draftId === draftId ? { ...item, text } : item)));
    }
    setRenaming(null);
  };

  const remove = (draftId: string) => {
    const index = items.findIndex((item) => item.draftId === draftId);
    if (index < 0) return;
    const removed = items[index];
    onChange?.(items.filter((item) => item.draftId !== draftId));
    toast.success('Your step has been deleted.', {
      duration: 6000,
      action: {
        label: 'Undo',
        onClick: () => {
          const current = latest.current;
          onChange?.([...current.slice(0, index), removed, ...current.slice(index)]);
        },
      },
    });
  };

  return (
    <section className={s.card} aria-label="Checklist" onClickCapture={swallowDismissClick}>
      <div className={s.top}>
        <div className={s.head}>
          <p className={s.title}>Checklist</p>
          {items.length > 0 && (
            <span className={s.count}>
              {done} of {items.length} done
            </span>
          )}
        </div>
        {items.length > 0 && (
          <ProgressBar
            value={(done / items.length) * 100}
            size="sm"
            label={`${done} of ${items.length} steps done`}
          />
        )}
      </div>

      {items.length > 0 && (
        <div className={s.list}>
          {items.map((item) =>
            renaming?.draftId === item.draftId ? (
              <StepInput
                key={`${item.draftId}-rename`}
                icon={<BiPencil size={20} />}
                value={renaming.text}
                placeholder={item.text}
                keepsOpen={false}
                onChange={(text) => setRenaming({ draftId: item.draftId, text })}
                onSave={saveRename}
                onCancel={() => setRenaming(null)}
              />
            ) : (
              <StepRow
                key={item.draftId}
                id={`${uid}-${item.draftId}`}
                item={item}
                editable={editable}
                onToggle={() => toggle(item.draftId)}
                onMenu={
                  canEditSteps ? (anchor) => setMenu({ draftId: item.draftId, anchor }) : undefined
                }
              />
            ),
          )}
        </div>
      )}

      {canEditSteps &&
        items.length < CHECKLIST_ITEMS_MAX &&
        (adding ? (
          <div ref={addRowRef}>
            <StepInput
              icon={<BiPlus size={20} />}
              value={draft}
              placeholder="Check the impeller for cracks"
              keepsOpen
              onChange={setDraft}
              onSave={addStep}
              onCancel={() => {
                setDraft('');
                setAdding(false);
              }}
            />
          </div>
        ) : (
          <AddButton label="Step" onClick={() => setAdding(true)} />
        ))}

      {canEditSteps && items.length > 0 && (
        <p className={s.hint}>Press and hold a step to rename or delete it.</p>
      )}

      <ContextMenu
        isOpen={!!menu}
        anchorEl={menu?.anchor}
        onClose={closeMenu}
        items={
          menu
            ? [
                {
                  label: 'Rename',
                  onClick: () =>
                    setRenaming({ draftId: menu.draftId, text: textOf(menu.draftId) ?? '' }),
                },
                { label: 'Delete', variant: 'danger', onClick: () => remove(menu.draftId) },
              ]
            : []
        }
      />
    </section>
  );
};
